package com.arena_web.core.viewmodel

import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.ClubAdminRepository
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.*

data class DashboardUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val hasNoClub: Boolean = false,
    val clubs: List<ClubDto> = emptyList(),
    val clubId: String? = null,
    val club: ClubDto? = null,

    // Stats
    val stats: DashboardStatsDto? = null,

    // Chart data per period (WEEK / MONTH / YEAR)
    val chartData: Map<String, DashboardChartDto> = emptyMap(),

    // Calendar / Schedule
    val calendar: BookingCalendarDto? = null,
    val currentWeekStart: LocalDate = currentMondayDate(),

    // Pending bookings
    val pendingBookings: List<BookingDto> = emptyList(),

    // Fields (for mapping fieldId → name/color)
    val fields: List<FieldDto> = emptyList(),
)

private fun currentMondayDate(): LocalDate {
    val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    val daysSinceMonday = today.dayOfWeek.isoDayNumber - 1
    return today.minus(daysSinceMonday, DateTimeUnit.DAY)
}

class DashboardViewModel(
    private val clubAdminRepository: ClubAdminRepository
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(DashboardUiState())
    val uiState: StateFlow<DashboardUiState> = _uiState.asStateFlow()

    override fun onCreate() {
        super.onCreate()
        loadClubAndData()
    }

    private fun loadClubAndData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }

            clubAdminRepository.getMyClubs().onSuccess { clubs ->
                _uiState.update { it.copy(clubs = clubs) }
                val savedId = Storage.get(StorageKeys.SELECTED_CLUB_ID)
                val club = clubs.find { it.id == savedId } ?: clubs.firstOrNull()
                if (club != null) {
                    Storage.save(StorageKeys.SELECTED_CLUB_ID, club.id)
                    _uiState.update { it.copy(clubId = club.id, club = club) }
                    loadAllData(club.id)
                } else {
                    _uiState.update { it.copy(isLoading = false, hasNoClub = true) }
                }
            }.onFailure { e ->
                _uiState.update { it.copy(isLoading = false, error = e.message) }
            }
        }
    }

    private fun loadAllData(clubId: String) {
        loadStats(clubId)
        loadCalendar(clubId)
        loadPendingBookings(clubId)
        loadFields(clubId)
        // Preload WEEK chart data
        loadChartData(clubId, "WEEK")
    }

    private fun loadStats(clubId: String) {
        viewModelScope.launch {
            clubAdminRepository.getDashboardStats(clubId, emptyMap())
                .onSuccess { stats ->
                    _uiState.update { it.copy(stats = stats) }
                }
        }
    }

    private fun loadCalendar(clubId: String) {
        viewModelScope.launch {
            val weekStart = _uiState.value.currentWeekStart.toString()
            clubAdminRepository.getBookingCalendar(clubId, mapOf("weekStart" to weekStart))
                .onSuccess { calendar ->
                    _uiState.update { it.copy(calendar = calendar, isLoading = false) }
                }.onFailure {
                    _uiState.update { it.copy(isLoading = false) }
                }
        }
    }

    private fun loadPendingBookings(clubId: String) {
        viewModelScope.launch {
            clubAdminRepository.getPendingBookings(clubId)
                .onSuccess { bookings ->
                    _uiState.update { it.copy(pendingBookings = bookings) }
                }
        }
    }

    private fun loadFields(clubId: String) {
        viewModelScope.launch {
            clubAdminRepository.getFields(clubId)
                .onSuccess { fields ->
                    _uiState.update { it.copy(fields = fields) }
                }
        }
    }

    private fun loadChartData(clubId: String, period: String) {
        viewModelScope.launch {
            clubAdminRepository.getDashboardChart(clubId, mapOf("period" to period))
                .onSuccess { chart ->
                    _uiState.update {
                        it.copy(chartData = it.chartData + (period to chart))
                    }
                }
        }
    }

    // ── Public actions ──

    fun requestChartData(period: String) {
        val state = _uiState.value
        if (state.chartData.containsKey(period)) return // already cached
        val clubId = state.clubId ?: return
        loadChartData(clubId, period)
    }

    fun createClub() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            clubAdminRepository.createClub(
                CreateClubRequest(name = "My Club", address = "")
            ).onSuccess { club ->
                _uiState.update {
                    it.copy(
                        clubId = club.id,
                        club = club,
                        clubs = it.clubs + club,
                        hasNoClub = false
                    )
                }
                loadAllData(club.id)
            }.onFailure {
                _uiState.update { it.copy(isLoading = false) }
            }
        }
    }

    fun switchClub(clubId: String) {
        val club = _uiState.value.clubs.find { it.id == clubId } ?: return
        Storage.save(StorageKeys.SELECTED_CLUB_ID, clubId)
        _uiState.update {
            it.copy(
                clubId = clubId,
                club = club,
                stats = null,
                calendar = null,
                pendingBookings = emptyList(),
                fields = emptyList(),
                chartData = emptyMap(),
                isLoading = true
            )
        }
        loadAllData(clubId)
    }

    fun navigateWeek(delta: Int) {
        val current = _uiState.value.currentWeekStart
        val newStart = current.plus(delta * 7, DateTimeUnit.DAY)
        _uiState.update { it.copy(currentWeekStart = newStart) }
        val clubId = _uiState.value.clubId ?: return
        loadCalendar(clubId)
    }

    fun goToThisWeek() {
        _uiState.update { it.copy(currentWeekStart = currentMondayDate()) }
        val clubId = _uiState.value.clubId ?: return
        loadCalendar(clubId)
    }

    fun approveBooking(bookingId: String) {
        val clubId = _uiState.value.clubId ?: return
        viewModelScope.launch {
            clubAdminRepository.approveBooking(clubId, bookingId).onSuccess {
                loadPendingBookings(clubId)
                loadCalendar(clubId)
            }
        }
    }

    fun rejectBooking(bookingId: String) {
        val clubId = _uiState.value.clubId ?: return
        viewModelScope.launch {
            clubAdminRepository.rejectBooking(clubId, bookingId).onSuccess {
                loadPendingBookings(clubId)
                loadCalendar(clubId)
            }
        }
    }

    fun markBookingPaid(bookingId: String) {
        val clubId = _uiState.value.clubId ?: return
        viewModelScope.launch {
            clubAdminRepository.markBookingPaid(clubId, bookingId).onSuccess {
                loadCalendar(clubId)
            }
        }
    }

    fun completeBooking(bookingId: String) {
        val clubId = _uiState.value.clubId ?: return
        viewModelScope.launch {
            clubAdminRepository.completeBooking(clubId, bookingId).onSuccess {
                loadCalendar(clubId)
            }
        }
    }

    fun startBooking(bookingId: String) {
        val clubId = _uiState.value.clubId ?: return
        viewModelScope.launch {
            clubAdminRepository.startBooking(clubId, bookingId).onSuccess {
                loadCalendar(clubId)
            }
        }
    }

    // ── Derived data helpers (for Schedule tab — still uses calendar) ──

    fun getWeekDayLabels(): List<String> {
        val days = _uiState.value.calendar?.days
        if (days != null && days.isNotEmpty()) {
            return days.map { day ->
                try {
                    val date = LocalDate.parse(day.date)
                    val dayName = date.dayOfWeek.name.take(3).lowercase()
                        .replaceFirstChar { it.uppercase() }
                    val monthName = date.month.name.take(3).lowercase()
                        .replaceFirstChar { it.uppercase() }
                    "$dayName, ${date.dayOfMonth} $monthName"
                } catch (_: Exception) {
                    day.date
                }
            }
        }
        // Fallback: generate from currentWeekStart
        val start = _uiState.value.currentWeekStart
        return (0..6).map { i ->
            val date = start.plus(i, DateTimeUnit.DAY)
            val dayName = date.dayOfWeek.name.take(3).lowercase()
                .replaceFirstChar { it.uppercase() }
            val monthName = date.month.name.take(3).lowercase()
                .replaceFirstChar { it.uppercase() }
            "$dayName, ${date.dayOfMonth} $monthName"
        }
    }
}
